'use client';

import { Dialog as SheetPrimitive } from 'radix-ui';
import { tv } from 'tailwind-variants';

import { body } from '../typography/Body';
import { heading } from '../typography/Heading';

import type { VariantProps } from 'tailwind-variants';

const sheet = tv({
  slots: {
    overlay: `
      fixed
      inset-0
      z-50
      bg-black/50
    `,
    content: `
      fixed
      z-50
      flex
      flex-col
      transition
      ease-in-out
      max-w-(--sheet-width)!
      outline-none
    `,
    close: `
      ring-offset-background
      focus:ring-ring
      data-[state=open]:bg-secondary
      rounded-xs
      opacity-70
      transition-opacity
      hover:opacity-100
      focus:ring-2
      focus:ring-offset-2
      focus:outline-hidden
      disabled:pointer-events-none
      cursor-pointer
    `,
    header: `
      flex
      flex-col
      gap-2
      sm:pb-6
      sm:px-8
      pb-4
      px-4
      mt-16
    `,
    footer: `
      mt-auto
      flex
      flex-col
      gap-6
      pt-4
      border-t
      w-full
    `,
  },
  variants: {
    disableAnimations: {
      false: {
        overlay: `
          data-[state=open]:animate-in
          data-[state=closed]:animate-out
          data-[state=closed]:fade-out-0
          data-[state=open]:fade-in-0
        `,
        content: `
          data-[state=open]:animate-in
          data-[state=closed]:animate-out
          data-[state=closed]:duration-300
          data-[state=open]:duration-500
        `,
      },
      true: {
        overlay: '',
        content: '',
      },
    },
    side: {
      top: {
        content: `
          data-[state=closed]:slide-out-to-top
          data-[state=open]:slide-in-from-top
          inset-x-0
          top-0
          h-auto
        `,
      },
      right: {
        content: `
          data-[state=closed]:slide-out-to-right
          data-[state=open]:slide-in-from-right
          inset-y-0
          right-0
          h-full
          w-full
          sm:max-w-sm
        `,
      },
      bottom: {
        content: `
          data-[state=closed]:slide-out-to-bottom
          data-[state=open]:slide-in-from-bottom
          inset-x-0
          bottom-0
          h-auto
        `,
      },
      left: {
        content: `
          data-[state=closed]:slide-out-to-left
          data-[state=open]:slide-in-from-left
          inset-y-0
          left-0
          h-full
          w-full
          sm:max-w-sm
        `,
      },
    },
    size: {
      default: {},
      lg: {
        content: 'max-w-[736px]!',
      },
      xlg: {
        content: 'max-w-[1104px]!',
      },
      full: {
        content: 'max-w-[90vw]!',
      },
    },
    bordered: {
      true: {
        header: 'border-b',
      },
    },
  },
});

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) {
  const { close } = sheet();
  return <SheetPrimitive.Close className={close({ className })} data-slot="sheet-close" {...props} />;
}

function SheetPortal({ ...props }: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({
  className,
  disableAnimations = false,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay> & {
  disableAnimations?: boolean;
}) {
  const { overlay } = sheet({ disableAnimations });
  return <SheetPrimitive.Overlay className={overlay({ className })} data-slot="sheet-overlay" {...props} />;
}

function SheetContent({
  className,
  children,
  side = 'right',
  size = 'default',
  disableAnimations = false,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: 'top' | 'right' | 'bottom' | 'left';
  size?: 'default' | 'lg' | 'xlg' | 'full';
  disableAnimations?: boolean;
}) {
  const { content } = sheet({ side, size });
  return (
    <SheetPortal>
      <SheetOverlay disableAnimations={disableAnimations} />
      <SheetPrimitive.Content
        className={content({ className, disableAnimations })}
        data-slot="sheet-content"
        {...props}
      >
        <div className="sm:m-3 bg-background h-full sm:rounded-lg sm:shadow-lg flex flex-col overflow-auto">
          {children}
        </div>
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

type SheetHeaderProps = React.ComponentProps<'div'> & {
  bordered?: boolean;
};

function SheetHeader({ className, bordered = true, ...props }: SheetHeaderProps) {
  const { header } = sheet();
  return <div className={header({ className, bordered })} data-slot="sheet-header" {...props} />;
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  const { footer } = sheet();
  return <div className={footer({ className })} data-slot="sheet-footer" {...props} />;
}

const sheetTitle = tv({
  extend: heading,
  defaultVariants: {
    size: 'lg',
    weight: 'medium',
  },
});

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title> & VariantProps<typeof sheetTitle>) {
  return <SheetPrimitive.Title className={sheetTitle({ className })} data-slot="sheet-title" {...props} />;
}

const sheetDescription = tv({
  extend: body,
  base: 'text-content-secondary',
  defaultVariants: {
    size: 'md',
  },
});

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description> & VariantProps<typeof sheetDescription>) {
  return (
    <SheetPrimitive.Description
      className={sheetDescription({ className, ...props })}
      data-slot="sheet-description"
      {...props}
    />
  );
}

export { Sheet, SheetTrigger, SheetClose, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription };
